<template>
  <div>
    <v-list :key="mainMenuListKey" dense>
      <v-list-item-group v-model="selectedMenuItem" color="secondary darken-2">
        <simple-tooltip
          v-for="menuItem in visibleMainMenuItems"
          :key="menuItem.key"
          :disabled="!menuFolded"
          :tooltip="menuItem.text"
        >
          <v-list-item
            :to="menuItem.path"
            @click="closeMenuGroups"
          >
            <v-list-item-icon>
              <v-icon v-text="menuItem.icon"></v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="menuItem.text"></v-list-item-title>
            </v-list-item-content>
            <v-avatar
              v-if="menuItem.badgeContentFunctionName && getBadgeValue(menuItem.badgeContentFunctionName) !== null"
              color="secondary"
              size="20"
            >
              <span class="white--text" style="font-size: 14px;">{{ getBadgeValue(menuItem.badgeContentFunctionName) }}</span>
            </v-avatar>
          </v-list-item>
        </simple-tooltip>
      </v-list-item-group>
      <v-list-group
        v-if="inactiveMainMenuItems.length > 0"
        v-model="moreMenuOpenModel"
        @click="unfoldMenu"
        no-action
        prepend-icon="mdi-dots-horizontal"
      >
        <template v-slot:activator>
          <v-list-item-content>
            <v-list-item-title>Mer</v-list-item-title>
          </v-list-item-content>
        </template>
        <v-list-item
          v-for="menuItem in visibleInactiveMainMenuItems"
          :key="menuItem.key"
          :to="menuItem.path"
          @click="closeMenuGroups"
          class="pl-4 v-list-item-extra-dense"
        >
          <v-list-item-icon>
            <v-icon v-text="menuItem.icon"></v-icon>
          </v-list-item-icon>
          <v-list-item-title v-text="menuItem.text"></v-list-item-title>
          <v-avatar
            v-if="menuItem.badgeContentFunctionName && getBadgeValue(menuItem.badgeContentFunctionName) !== null"
            color="secondary"
            size="20"
          >
            <span class="white--text" style="font-size: 14px;">{{ getBadgeValue(menuItem.badgeContentFunctionName) }}</span>
          </v-avatar>
        </v-list-item>
        <div class="full-width py-2 px-2 d-flex flex-column">
          <v-btn
            outlined
            small
            block
            color="primary"
            class="mx-auto"
            @click="openMainMenuSettingsDialog"
          >
            <v-icon class="mr-3">mdi-playlist-edit</v-icon>
            <span>Anpassa menyn</span>
          </v-btn>
          <div v-show="hiddenMainMenuItems.length > 0" class="text-center text-sm pt-1 disabled--text">+{{ hiddenMainMenuItems.length }} dolda länkar</div>
        </div>
      </v-list-group>
    </v-list>
    <v-dialog v-model="showMainMenuSettingsDialog" max-width="520" :fullscreen="isMobile">
      <v-card class="full-height overflow-y-auto" style="max-height: 90vh;">
        <div class="px-4 pt-4">
          <v-alert text type="info" dense>
            Dra och släpp för att ändra ordning på länkarna i huvudmenyn
          </v-alert>
          <div class="overflow-y-auto">
            <v-card-text>
              <vue-draggable
                v-model="activeDialogMenuItems"
                :group="{ name: 'main-menu-items' }"
                :animation="220"
                easing="cubic-bezier(0.2, 0, 0, 1)"
                handle=".main-menu-settings-dialog__drag-handle"
                ghost-class="main-menu-settings-dialog__ghost"
                chosen-class="main-menu-settings-dialog__chosen"
                class="main-menu-settings-dialog__list"
              >
                <v-list-item
                  v-for="menuItem in activeDialogMenuItems"
                  :key="menuItem.key"
                  dense
                  class="main-menu-settings-dialog__item"
                >
                  <v-list-item-icon class="main-menu-settings-dialog__drag-handle cursor-move">
                    <v-icon>mdi-drag</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon v-text="menuItem.icon" class="mr-3"></v-icon>
                      {{ menuItem.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <simple-tooltip :tooltip="getVisitCountText(menuItem.key)">
                    <v-chip
                      x-small
                      label
                      color="secondary"
                      text-color="white"
                    >
                      {{ getVisitCount(menuItem.key) }}
                    </v-chip>
                  </simple-tooltip>
                </v-list-item>
              </vue-draggable>

              <v-divider class="mt-3" style="border-color: #aaa"></v-divider>
              <div class="mb-3 pt-2 full-width d-flex justify-center">
                <span class="text-caption">
                  <v-icon small class="mr-4">mdi-chevron-down</v-icon>
                  Länkar under "Mer"
                  <v-icon small class="ml-4">mdi-chevron-down</v-icon></span>
              </div>

              <div class="d-flex justify-end mb-2 pr-4">
                <simple-tooltip :tooltip="visitsSortingTooltip">
                  <v-btn
                    icon
                    small
                    :color="sortInactiveDialogMenuItemsByVisits ? 'secondary' : 'grey'"
                    :aria-pressed="sortInactiveDialogMenuItemsByVisits.toString()"
                    aria-label="Sortera länkar under Mer efter antal besök"
                    @click="toggleVisitsSorting"
                  >
                    <v-icon>mdi-sort-ascending</v-icon>
                  </v-btn>
                </simple-tooltip>
              </div>

              <vue-draggable
                v-model="inactiveDialogMenuItems"
                :group="{ name: 'main-menu-items' }"
                :animation="220"
                easing="cubic-bezier(0.2, 0, 0, 1)"
                handle=".main-menu-settings-dialog__drag-handle"
                ghost-class="main-menu-settings-dialog__ghost"
                chosen-class="main-menu-settings-dialog__chosen"
                class="main-menu-settings-dialog__list"
                @change="handleInactiveDialogMenuItemsChanged"
              >
                <v-list-item
                  v-for="menuItem in inactiveDialogMenuItems"
                  :key="menuItem.key"
                  dense
                  class="main-menu-settings-dialog__item"
                >
                  <v-list-item-icon class="main-menu-settings-dialog__drag-handle cursor-move">
                    <v-icon>mdi-drag</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon v-text="menuItem.icon" class="mr-3"></v-icon>
                      {{ menuItem.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <simple-tooltip :tooltip="getVisitCountText(menuItem.key)">
                    <v-chip
                      x-small
                      label
                      color="secondary"
                      text-color="white"
                    >
                      {{ getVisitCount(menuItem.key) }}
                    </v-chip>
                  </simple-tooltip>
                </v-list-item>
              </vue-draggable>

              <v-divider class="mt-3" style="border-color: #aaa"></v-divider>
              <div class="mb-3 pt-2 full-width d-flex justify-center">
                <span class="text-caption">
                  <v-icon small class="mr-4">mdi-chevron-down</v-icon>
                  Länkar dolda från menyn
                  <v-icon small class="ml-4">mdi-chevron-down</v-icon></span>
              </div>

              <vue-draggable
                v-model="hiddenDialogMenuItems"
                :group="{ name: 'main-menu-items' }"
                :animation="220"
                easing="cubic-bezier(0.2, 0, 0, 1)"
                handle=".main-menu-settings-dialog__drag-handle"
                ghost-class="main-menu-settings-dialog__ghost"
                chosen-class="main-menu-settings-dialog__chosen"
                class="main-menu-settings-dialog__list"
              >
                <v-list-item
                  v-for="menuItem in hiddenDialogMenuItems"
                  :key="menuItem.key"
                  dense
                  class="main-menu-settings-dialog__item"
                >
                  <v-list-item-icon class="main-menu-settings-dialog__drag-handle cursor-move">
                    <v-icon>mdi-drag</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon v-text="menuItem.icon" class="mr-3"></v-icon>
                      {{ menuItem.text }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <simple-tooltip :tooltip="getVisitCountText(menuItem.key)">
                    <v-chip
                      x-small
                      label
                      color="secondary"
                      text-color="white"
                    >
                      {{ getVisitCount(menuItem.key) }}
                    </v-chip>
                  </simple-tooltip>
                </v-list-item>
              </vue-draggable>

            </v-card-text>
            <v-card-actions>
              <v-btn
                @click="closeMainMenuSettingsDialog"
                text
                color="primary"
              >
                Avbryt
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                @click="saveMainMenuSettings"
                color="success"
                :loading="isSavingMainMenuSettings"
                :disabled="activeDialogMenuItems.length === 0"
              >
                Spara
              </v-btn>
            </v-card-actions>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.main-menu-settings-dialog__list {
  min-height: 40px;
}

.main-menu-settings-dialog__ghost {
  opacity: 0;
}

.main-menu-settings-dialog__chosen {
  background-color: rgba(0, 0, 0, 0.04);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.12);
}
</style>

<script>
import { mapGetters } from 'vuex'
import VueDraggable from 'vuedraggable'
import SimpleTooltip from '@/components/common/SimpleTooltip.vue'
import { getMainMenuVisitTotals, recordMainMenuVisit } from '@/utils/mainMenuVisits'

export default {
  name: 'MainMenu',
  components: {
    VueDraggable,
    SimpleTooltip
  },
  props: {
    menuFolded: Boolean
  },
  data: () => ({
    selectedMenuItem: 0,
    moreMenuOpen: false,
    showMainMenuSettingsDialog: false,
    activeDialogMenuItems: [],
    inactiveDialogMenuItems: [],
    customInactiveDialogMenuItems: [],
    hiddenDialogMenuItems: [],
    isSavingMainMenuSettings: false,
    sortInactiveDialogMenuItemsByVisits: false
  }),
  computed: {
    ...mapGetters(
      {
        activeAuthSession: 'user/activeAuthSession',
        hasListLibrary: 'listLibrary/hasLists',
        activeMainMenuItems: 'user/activeMainMenuItems',
        inactiveMainMenuItems: 'user/inactiveMainMenuItems',
        hiddenMainMenuItems: 'user/hiddenMainMenuItems',
        unresolvedBookvisitFailedSyncCount: 'bookvisit/unresolvedFailedSyncCount'
      }
    ),
    user: function () {
      return this.activeAuthSession?.user
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.smAndDown
    },
    mainMenuConfig: function () {
      return this.user?.main_menu_config || {}
    },
    sortInactiveMainMenuItemsByVisits: function () {
      return this.mainMenuConfig.sort_inactive_main_menu_items_by_visits !== false // defaultar till true om inte sparats
    },
    visitsSortingTooltip: function () {
      return this.sortInactiveDialogMenuItemsByVisits
        ? 'Sortering på mest använda är aktiv. Klicka för att använda anpassad ordning.'
        : 'Sortera länkar under "Mer" efter antal besök de senaste 60 dagarna.'
    },
    mainMenuPageVisits: function () {
      return this.$store.state.sessionCache.mainMenuPageVisits || {}
    },
    mainMenuVisitTotals: function () {
      return getMainMenuVisitTotals(this.mainMenuPageVisits)
    },
    orderedInactiveMainMenuItems: function () {
      if (!this.sortInactiveMainMenuItemsByVisits) {
        return this.inactiveMainMenuItems
      }

      return this.sortMenuItemsByVisits(this.inactiveMainMenuItems)
    },
    currentInactiveMenuItem: function () {
      return this.orderedInactiveMainMenuItems.find(menuItem => this.isCurrentMenuItem(menuItem))
    },
    moreMenuOpenModel: {
      get: function () {
        return this.moreMenuOpen || Boolean(this.currentInactiveMenuItem)
      },
      set: function (isOpen) {
        this.moreMenuOpen = isOpen || Boolean(this.currentInactiveMenuItem)
      }
    },
    visibleMainMenuItems: function () {
      return this.activeMainMenuItems
    },
    visibleInactiveMainMenuItems: function () {
      return this.orderedInactiveMainMenuItems
    },
    mainMenuListKey: function () {
      // Detta är för att tvinga omrendering av listan när användaren sparat ändringar i huvudmenyn, annars uppdateras inte listan korrekt
      return [
        this.visibleMainMenuItems.map(menuItem => menuItem.key).join(','),
        this.visibleInactiveMainMenuItems.map(menuItem => menuItem.key).join(','),
        this.hiddenMainMenuItems.map(menuItem => menuItem.key).join(',')
      ].join('|')
    },
    orderNotesCount: function () {
      if (this.$store.state.posOrder.orders.length === 0) {
        return null // Inte visa 0
      }
      return this.$store.state.posOrder.orders.length
    }
  },
  methods: {
    getBadgeValue: function (computedName) {
      if (!computedName) { return null }
      return this[computedName]
    },
    closeMenuGroups: function () {
      this.moreMenuOpen = false
    },
    openMainMenuSettingsDialog: function () {
      this.closeMenuGroups()
      this.activeDialogMenuItems = [...this.activeMainMenuItems]
      this.inactiveDialogMenuItems = [...this.inactiveMainMenuItems]
      this.customInactiveDialogMenuItems = [...this.inactiveMainMenuItems]
      this.sortInactiveDialogMenuItemsByVisits = this.sortInactiveMainMenuItemsByVisits
      this.sortInactiveDialogItems()

      // Filtrerar bort menylänkar som är restricted från user role
      const userRoleHiddenMenuItemKeys = this.user?.role?.hidden_menu_items || []
      const hiddenMainMenuItems = [...this.hiddenMainMenuItems].filter(menuItem => !userRoleHiddenMenuItemKeys.includes(menuItem.key))

      this.hiddenDialogMenuItems = hiddenMainMenuItems
      this.showMainMenuSettingsDialog = true
    },
    closeMainMenuSettingsDialog: function () {
      this.showMainMenuSettingsDialog = false
    },
    saveMainMenuSettings: function () {
      this.isSavingMainMenuSettings = true

      const mainMenuConfig = {
        active_main_menu_items: this.activeDialogMenuItems.map(menuItem => menuItem.key),
        hidden_main_menu_items: this.hiddenDialogMenuItems.map(menuItem => menuItem.key),
        sort_inactive_main_menu_items_by_visits: this.sortInactiveDialogMenuItemsByVisits,
        menu_item_order: [
          ...this.activeDialogMenuItems,
          ...this.inactiveDialogMenuItems,
          ...this.hiddenDialogMenuItems
        ].map(menuItem => menuItem.key)
      }

      this.$store.dispatch('user/saveMainMenuConfig', { mainMenuConfig })
        .then((data) => {
          if (data.status === 'success') {
            this.closeMainMenuSettingsDialog()
          }
        })
        .finally(() => {
          this.isSavingMainMenuSettings = false
        })
    },
    getMenuRootPath: function (menuPath) {
      if (menuPath === '/') {
        return '/'
      }

      return `/${menuPath.split('/').filter(Boolean)[0]}`
    },
    isCurrentMenuItem: function (menuItem) {
      const currentPath = this.$route.path
      const menuRootPath = this.getMenuRootPath(menuItem.path)

      if (menuRootPath === '/') {
        return currentPath === '/'
      }

      return currentPath === menuRootPath || currentPath.startsWith(`${menuRootPath}/`)
    },
    sortMenuItemsByVisits: function (menuItems) {
      return [...menuItems].sort((firstItem, secondItem) => {
        const countDifference = this.getVisitCount(secondItem.key) - this.getVisitCount(firstItem.key)
        if (countDifference !== 0) {
          return countDifference
        }

        return menuItems.indexOf(firstItem) - menuItems.indexOf(secondItem)
      })
    },
    sortInactiveDialogItems: function () {
      if (this.sortInactiveDialogMenuItemsByVisits) {
        this.inactiveDialogMenuItems = this.sortMenuItemsByVisits(this.inactiveDialogMenuItems)
      }
    },
    handleVisitsSortingChanged: function (sortByVisits) {
      if (sortByVisits) {
        this.sortInactiveDialogItems()
        return
      }

      this.inactiveDialogMenuItems = [...this.customInactiveDialogMenuItems]
    },
    toggleVisitsSorting: function () {
      this.sortInactiveDialogMenuItemsByVisits = !this.sortInactiveDialogMenuItemsByVisits
      this.handleVisitsSortingChanged(this.sortInactiveDialogMenuItemsByVisits)
    },
    handleInactiveDialogMenuItemsChanged: function (event) {
      if (event.moved) {
        this.customInactiveDialogMenuItems = [...this.inactiveDialogMenuItems]
        this.sortInactiveDialogMenuItemsByVisits = false
        return
      }

      if (event.added) {
        const customMenuItems = this.customInactiveDialogMenuItems.filter(menuItem => menuItem.key !== event.added.element.key)
        customMenuItems.splice(event.added.newIndex, 0, event.added.element)
        this.customInactiveDialogMenuItems = customMenuItems

        if (this.sortInactiveDialogMenuItemsByVisits) {
          this.sortInactiveDialogItems()
        }
        return
      }

      if (event.removed) {
        this.customInactiveDialogMenuItems = this.customInactiveDialogMenuItems.filter(menuItem => menuItem.key !== event.removed.element.key)
        return
      }

      if (!this.sortInactiveDialogMenuItemsByVisits) {
        this.customInactiveDialogMenuItems = [...this.inactiveDialogMenuItems]
      }
    },
    getVisitCount: function (menuItemKey) {
      return this.mainMenuVisitTotals[menuItemKey] || 0
    },
    getVisitCountText: function (menuItemKey) {
      const visitCount = this.getVisitCount(menuItemKey)
      return `${visitCount} besök senaste 60 dagarna`
    },
    recordCurrentPageVisit: function () {
      if (!this.activeAuthSession) {
        return
      }

      const menuItem = this.$store.state.user.mainMenuItems.find(menuItem => this.isCurrentMenuItem(menuItem))
      if (!menuItem) {
        return
      }

      const mainMenuPageVisits = recordMainMenuVisit(this.mainMenuPageVisits, menuItem.key)
      this.$store.commit('sessionCache/SET_MAIN_MENU_PAGE_VISITS', mainMenuPageVisits)
    },
    unfoldMenu: function () {
      this.$emit('unfold-menu')
    }
  },
  watch: {
    currentInactiveMenuItem: function (menuItem) {
      this.moreMenuOpen = Boolean(menuItem)
    },
    '$route.path': {
      immediate: true,
      handler: function () {
        this.recordCurrentPageVisit()
      }
    }
  }
}
</script>
